import './SlidingMenu.css'

import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { getSectionLabel } from '../../Services/MenuConfig'
import { cap } from '../../State'
import { translations } from '../../translations'
import { Icon } from './Icon'
import noraLogoSvg from './images/nora-logo.svg'

const language = cap.language.state || 'IT'
const t = translations[language]

const MOBILE_BREAKPOINT = '(max-width: 900px)'

/**
 * Restituisce true se il viewport corrisponde al layout mobile della sidebar
 * @returns {boolean}
 */
const isMobileViewport = () => window.matchMedia(MOBILE_BREAKPOINT).matches

export const HamburgerMenu = ({ onToggle = () => {} }) => (
  <button
    type="button"
    className="nora-icon-btn nora-burger"
    aria-label={t.apriChiudiMenu}
    onClick={() => onToggle()}
  >
    <Icon name="menu" size={18} />
  </button>
)

/**
 * Verifica se un percorso del menu corrisponde alla pagina corrente
 * @param {string} itemPath - Percorso della voce di menu
 * @param {string} currentPath - Percorso corrente del browser
 * @returns {boolean} true se la voce è attiva
 */
const isMenuItemActive = (itemPath, currentPath) => {
  if (!itemPath || itemPath === 'logout') return false
  const normalizedItem = itemPath.replace(/\/+$/, '')
  const normalizedCurrent = currentPath.replace(/\/+$/, '')
  if (normalizedItem === normalizedCurrent) return true
  return normalizedCurrent.startsWith(normalizedItem + '/')
}

/**
 * Raggruppa le voci di menu per sezione, preservando l'ordine originale
 * @param {Array} items - Lista delle voci di menu
 * @returns {Array<{section: string, items: Array}>} Voci raggruppate per sezione
 */
const groupBySection = (items) => {
  const groups = []
  const sectionMap = {}
  items.forEach((item) => {
    if (item.path === 'logout') return
    const section = item.section || 'other'
    if (!sectionMap[section]) {
      sectionMap[section] = { section, items: [] }
      groups.push(sectionMap[section])
    }
    sectionMap[section].items.push(item)
  })
  return groups
}

/**
 * Sidebar di navigazione persistente con collapse desktop e drawer mobile
 * @param {Object} props
 * @param {Function} props.menuAction - Callback navigazione voce menu
 * @param {Array} props.menuItems - Voci del menu
 * @param {boolean} props.collapsed - Sidebar ridotta (solo icone) su desktop
 * @param {boolean} props.mobileOpen - Drawer aperto su mobile
 * @param {Function} props.onCloseMobile - Chiude il drawer mobile
 */
export const Sidebar = ({
  menuAction,
  menuItems,
  collapsed = false,
  mobileOpen = false,
  onCloseMobile
}) => {
  const location = useLocation()
  const sections = useMemo(() => groupBySection(menuItems), [menuItems])
  const logoutItem = useMemo(() => menuItems.find((item) => item.path === 'logout'), [menuItems])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileOpen) onCloseMobile()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mobileOpen, onCloseMobile])

  const handleItemClick = (path) => {
    menuAction(path)
    if (isMobileViewport()) onCloseMobile()
  }

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className="nora-side-backdrop"
          aria-label={t.chiudiMenu}
          onClick={onCloseMobile}
        />
      )}
      <aside
        className={[
          'nora-side',
          collapsed ? 'is-collapsed' : '',
          mobileOpen ? 'is-mobile-open' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="nora-side__brand">
          <img src={noraLogoSvg} alt="NORA" className="nora-side__logo nora-side__logo--solid" />
          <div
            className="nora-side__logo nora-side__logo--gradient"
            role="img"
            aria-label="NORA"
            style={{ WebkitMaskImage: `url(${noraLogoSvg})`, maskImage: `url(${noraLogoSvg})` }}
          />
        </div>

        {sections.map(({ section, items }) => (
          <div key={section} className="nora-side__group">
            <div className="nora-side__group-label">{getSectionLabel(section)}</div>
            {items.map(({ path, label, icon, badge, disabled }) => {
              if (disabled) return null
              const isActive = isMenuItemActive(path, location.pathname)
              return (
                <button
                  key={path}
                  type="button"
                  className={`nora-side__item ${isActive ? 'is-active' : ''}`}
                  title={collapsed ? label : undefined}
                  onClick={() => handleItemClick(path)}
                >
                  <Icon name={icon} size={16} />
                  <span className="nora-side__item-label">{label}</span>
                  {badge != null && <span className="nora-side__item-badge">{badge}</span>}
                </button>
              )
            })}
          </div>
        ))}

        {logoutItem && (
          <div className="nora-side__group nora-side__group--logout">
            <button
              type="button"
              className="nora-side__item"
              title={collapsed ? logoutItem.label : undefined}
              onClick={() => handleItemClick('logout')}
            >
              <Icon name={logoutItem.icon} size={16} />
              <span className="nora-side__item-label">{logoutItem.label}</span>
            </button>
          </div>
        )}
      </aside>
    </>
  )
}

/** @deprecated Usare Sidebar — mantenuto per compatibilità import */
export const SlidingMenu = () => null
